import { useState, useEffect, useRef, useCallback } from 'react';
import {
  ChevronLeft, ChevronRight, Upload, Trash2, Plus,
  Loader2, ImagePlus, X, Edit2, Check, Link
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TokenSidebar({ open, onToggle, onPlaceToken, onClearAllTokens, placedTokenCount }) {
  const [tokenDefs, setTokenDefs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [addingState, setAddingState] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteTyped, setDeleteTyped] = useState('');
  const [renaming, setRenaming] = useState(null);
  const [renameVal, setRenameVal] = useState('');
  const [expandedToken, setExpandedToken] = useState(null);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlValue, setUrlValue] = useState('');
  const [urlName, setUrlName] = useState('');
  const [urlLoading, setUrlLoading] = useState(false);
  const fileRef = useRef(null);
  const stateFileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tokens`);
      setTokenDefs(res.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/tokens/upload`, fd);
      await load();
    } catch (err) { console.error(err); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleUrlSubmit = async () => {
    if (!urlValue.trim()) return;
    setUrlLoading(true);
    try {
      await axios.post(`${API}/tokens/from-url`, {
        url: urlValue.trim(),
        name: urlName.trim() || 'URL Token',
      });
      await load();
      setUrlValue('');
      setUrlName('');
      setShowUrlInput(false);
    } catch (err) { console.error(err); }
    setUrlLoading(false);
  };

  const handleAddState = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !addingState) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/tokens/${addingState}/states`, fd);
      await load();
      setAddingState(null);
    } catch (err) { console.error(err); }
    if (stateFileRef.current) stateFileRef.current.value = '';
  };

  const handleDelete = async () => {
    if (!deleteTarget || deleteTyped !== deleteTarget.name) return;
    try {
      await axios.delete(`${API}/tokens/${deleteTarget.id}`);
      await load();
      setDeleteTarget(null);
      setDeleteTyped('');
    } catch (e) { console.error(e); }
  };

  const getTokenImg = (token) => {
    const states = token.states || [];
    const first = states[0];
    if (first?.image_url) return first.image_url;
    // Use thumbnail for sidebar display
    if (first?.file_id) return `${API}/files/${first.file_id}/thumb`;
    if (token.file_id) return `${API}/files/${token.file_id}/thumb`;
    return '';
  };

  return (
    <>
      {/* Toggle Tab */}
      <button
        onClick={onToggle}
        className={`fixed top-1/2 -translate-y-1/2 z-50 flex items-center justify-center w-6 h-16 rounded-r-lg transition-all duration-300 ${
          open ? 'left-[260px] bg-emerald-600 text-black' : 'left-0 bg-zinc-800 border border-l-0 border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-700'
        }`}
        data-testid="toggle-sidebar-btn"
      >
        {open ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
      </button>

      {/* Sidebar */}
      <div
        className={`fixed top-0 left-0 h-full z-40 w-[260px] glass-panel rounded-none border-l-0 border-t-0 border-b-0 transition-transform duration-300 flex flex-col ${
          open ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="token-sidebar"
      >
        {/* Header */}
        <div className="px-3 py-2.5 border-b border-white/5 flex items-center justify-between shrink-0">
          <span className="text-[10px] font-semibold text-zinc-500 uppercase tracking-widest" style={{ fontFamily: 'Cinzel, serif' }}>
            Tokens
          </span>
          <span className="text-[10px] text-zinc-600 font-mono">{tokenDefs.length} def{tokenDefs.length !== 1 ? 's' : ''}</span>
        </div>

        {/* Upload + Actions */}
        <div className="px-3 py-2 border-b border-white/5 space-y-1.5 shrink-0">
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} data-testid="token-upload-input" />
          <div className="flex gap-1.5">
            <Button
              size="sm"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-black font-semibold h-7 text-xs"
              data-testid="sidebar-upload-btn"
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
              Upload
            </Button>
            <Button
              size="sm"
              onClick={() => setShowUrlInput(!showUrlInput)}
              className={`h-7 text-xs px-2.5 ${showUrlInput ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'bg-zinc-800 text-zinc-400 border border-zinc-700 hover:text-white'}`}
              data-testid="sidebar-url-btn"
            >
              <Link className="w-3.5 h-3.5 mr-1" /> URL
            </Button>
          </div>

          {/* URL Token Input */}
          {showUrlInput && (
            <div className="space-y-1.5 pt-1 animate-fade-in">
              <Input
                value={urlName}
                onChange={(e) => setUrlName(e.target.value)}
                placeholder="Token name (optional)"
                className="h-7 text-xs bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-600"
                data-testid="url-token-name"
              />
              <div className="flex gap-1">
                <Input
                  value={urlValue}
                  onChange={(e) => setUrlValue(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleUrlSubmit()}
                  placeholder="https://...image.png"
                  className="h-7 text-xs bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-600 flex-1"
                  data-testid="url-token-input"
                />
                <Button
                  size="sm"
                  onClick={handleUrlSubmit}
                  disabled={!urlValue.trim() || urlLoading}
                  className="h-7 px-2 bg-amber-600 hover:bg-amber-500 text-black"
                  data-testid="url-token-submit"
                >
                  {urlLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                </Button>
              </div>
            </div>
          )}

          {placedTokenCount > 0 && onClearAllTokens && (
            <Button
              size="sm" variant="ghost"
              onClick={onClearAllTokens}
              className="w-full h-6 text-[10px] text-zinc-500 hover:text-red-400"
              data-testid="clear-all-tokens-btn"
            >
              <X className="w-3 h-3 mr-1" /> Clear all placed ({placedTokenCount})
            </Button>
          )}
        </div>

        {/* Token List */}
        <ScrollArea className="flex-1 min-h-0">
          <div className="px-2 py-1.5">
            {tokenDefs.length === 0 ? (
              <div className="text-center py-6 text-zinc-600 text-[11px]">
                No tokens yet. Upload one above.
              </div>
            ) : (
              <div className="space-y-0.5">
                {tokenDefs.map((td) => {
                  const imgUrl = getTokenImg(td);
                  const stateCount = (td.states || []).length;
                  const isExpanded = expandedToken === td.id;
                  const isRenaming = renaming === td.id;

                  return (
                    <div key={td.id} className="rounded-md overflow-hidden" data-testid={`sidebar-token-${td.id}`}>
                      {/* Token Row */}
                      <div
                        className={`flex items-center gap-2 px-2 py-1.5 cursor-pointer transition-colors group ${
                          isExpanded ? 'bg-white/5' : 'hover:bg-white/[0.03]'
                        }`}
                        onClick={() => setExpandedToken(isExpanded ? null : td.id)}
                      >
                        <div className="w-8 h-8 rounded overflow-hidden bg-zinc-800 border border-zinc-700 shrink-0">
                          {imgUrl && <img src={imgUrl} alt={td.name} className="w-full h-full object-cover" />}
                        </div>

                        <div className="flex-1 min-w-0">
                          {isRenaming ? (
                            <div className="flex items-center gap-1">
                              <input
                                value={renameVal}
                                onChange={(e) => setRenameVal(e.target.value)}
                                className="bg-zinc-900 border border-zinc-700 rounded text-[11px] text-white px-1.5 py-0.5 w-full"
                                autoFocus
                                onKeyDown={(e) => { if (e.key === 'Enter') { setRenaming(null); } if (e.key === 'Escape') { setRenaming(null); } }}
                                onClick={(e) => e.stopPropagation()}
                              />
                              <button onClick={(e) => { e.stopPropagation(); setRenaming(null); }} className="text-emerald-400">
                                <Check className="w-3 h-3" />
                              </button>
                            </div>
                          ) : (
                            <p className="text-[11px] font-medium text-zinc-300 truncate leading-tight">{td.name}</p>
                          )}
                          {stateCount > 1 && <p className="text-[9px] text-amber-500/70 font-mono leading-tight">{stateCount} states</p>}
                        </div>

                        {/* Quick place */}
                        <button
                          className="p-1 rounded hover:bg-emerald-500/20 text-zinc-600 hover:text-emerald-400 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                          onClick={(e) => { e.stopPropagation(); onPlaceToken(td); }}
                          data-testid={`place-token-${td.id}`}
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>

                      {/* Expanded Actions */}
                      {isExpanded && (
                        <div className="px-2 pb-2 pt-1 bg-white/[0.02] border-t border-white/5 animate-fade-in">
                          {/* States preview */}
                          {stateCount > 0 && (
                            <div className="flex gap-1 mb-2">
                              {td.states.map((state, si) => {
                                const stateImgUrl = state.image_url || (state.file_id ? `${API}/files/${state.file_id}/thumb` : '');
                                return (
                                  <Tooltip key={si}>
                                    <TooltipTrigger asChild>
                                      <div className="w-7 h-7 rounded overflow-hidden bg-zinc-800 border border-zinc-700">
                                        {stateImgUrl && <img src={stateImgUrl} alt={state.name} className="w-full h-full object-cover" />}
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent side="top">{state.name}</TooltipContent>
                                  </Tooltip>
                                );
                              })}
                            </div>
                          )}

                          {/* Action buttons */}
                          <div className="flex gap-1 flex-wrap">
                            <button
                              className="flex items-center gap-1 px-2 py-1 rounded text-[10px] bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-colors"
                              onClick={(e) => { e.stopPropagation(); onPlaceToken(td); }}
                              data-testid={`expanded-place-${td.id}`}
                            >
                              <Plus className="w-3 h-3" /> Place
                            </button>

                            <button
                              className="flex items-center gap-1 px-2 py-1 rounded text-[10px] bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 transition-colors"
                              onClick={(e) => { e.stopPropagation(); setAddingState(td.id); stateFileRef.current?.click(); }}
                              data-testid={`expanded-add-state-${td.id}`}
                            >
                              <ImagePlus className="w-3 h-3" /> Add State
                            </button>

                            <button
                              className="flex items-center gap-1 px-2 py-1 rounded text-[10px] bg-zinc-800 text-zinc-400 hover:bg-zinc-700 transition-colors"
                              onClick={(e) => { e.stopPropagation(); setRenaming(td.id); setRenameVal(td.name); }}
                            >
                              <Edit2 className="w-3 h-3" /> Rename
                            </button>

                            <button
                              className="flex items-center gap-1 px-2 py-1 rounded text-[10px] bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
                              onClick={(e) => { e.stopPropagation(); setDeleteTarget(td); }}
                              data-testid={`expanded-delete-${td.id}`}
                            >
                              <Trash2 className="w-3 h-3" /> Delete
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Help Footer */}
        <div className="px-3 py-2 border-t border-white/5 text-[9px] text-zinc-600 leading-relaxed shrink-0 grid grid-cols-2 gap-x-3">
          <p><span className="text-zinc-500 font-mono">A / D</span> Rotate</p>
          <p><span className="text-zinc-500 font-mono">+ / -</span> Scale</p>
          <p><span className="text-zinc-500 font-mono">DblClk</span> Cycle state</p>
          <p><span className="text-zinc-500 font-mono">RClick</span> Remove</p>
          <p><span className="text-zinc-500 font-mono">Del</span> Delete</p>
          <p><span className="text-zinc-500 font-mono">Drag</span> Move</p>
        </div>
      </div>

      {/* Hidden state file input */}
      <input ref={stateFileRef} type="file" accept="image/*" className="hidden" onChange={handleAddState} />

      {/* Delete Dialog */}
      {deleteTarget && (
        <Dialog open={!!deleteTarget} onOpenChange={(v) => { if (!v) { setDeleteTarget(null); setDeleteTyped(''); } }}>
          <DialogContent className="bg-zinc-950 border-zinc-800 max-w-sm z-[100]" data-testid="delete-token-dialog">
            <DialogHeader>
              <DialogTitle className="text-red-400" style={{ fontFamily: 'Cinzel, serif' }}>Delete Token</DialogTitle>
              <DialogDescription className="text-zinc-500">
                Type <span className="text-red-400 font-mono font-bold">{deleteTarget.name}</span> to confirm
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 mt-2">
              <Input
                value={deleteTyped}
                onChange={(e) => setDeleteTyped(e.target.value)}
                placeholder={`Type "${deleteTarget.name}"`}
                className="bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-600 font-mono"
                data-testid="delete-token-confirm-input"
                autoFocus
              />
              <Button
                onClick={handleDelete}
                disabled={deleteTyped !== deleteTarget.name}
                className={`w-full font-semibold ${deleteTyped === deleteTarget.name ? 'bg-red-600 hover:bg-red-500 text-white' : 'bg-zinc-800 text-zinc-500'}`}
                data-testid="delete-token-confirm-btn"
              >
                <Trash2 className="w-4 h-4 mr-1.5" /> Delete Token
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
